import { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Logo } from "../shared/Logo";
import { ThemeToggle } from "../shared/ThemeToggle";
import { NotificationBell } from "./NotificationBell";
import { AISidePanel } from "../shared/AISidePanel";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../ui/button";
import { CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "../ui/command";
import {
  LayoutDashboard, Inbox, KanbanSquare, Rocket, CalendarDays, Radio, Users, UserRound,
  Receipt, BarChart3, Sparkles, Settings, LogOut, Search, Menu, X, CircleDollarSign, PiggyBank, Headset, Calculator, Link2, Boxes, ShieldCheck,
} from "lucide-react";

const NAV_GROUPS = [
  {
    label: "Command",
    items: [
      { to: "/ops", label: "Dashboard", icon: LayoutDashboard, end: true },
      { to: "/ops/missions", label: "Mission Board", icon: Rocket },
      { to: "/ops/create-job", label: "Create Job Link", icon: Link2 },
      { to: "/ops/calendar", label: "Calendar", icon: CalendarDays },
      { to: "/ops/dispatch", label: "Dispatch", icon: Radio },
      { to: "/ops/resources", label: "Resources", icon: Boxes },
    ],
  },
  {
    label: "Pipeline",
    items: [
      { to: "/ops/quotes", label: "Quote Requests", icon: Inbox },
      { to: "/ops/quote-generator", label: "Quote Generator", icon: Calculator },
      { to: "/ops/crm", label: "CRM Pipeline", icon: KanbanSquare },
      { to: "/ops/customers", label: "Customers", icon: UserRound },
      { to: "/ops/support", label: "Support Inbox", icon: Headset },
    ],
  },
  {
    label: "Finance",
    items: [
      { to: "/ops/invoices", label: "Invoices", icon: Receipt },
      { to: "/ops/pricing", label: "Pricing & Payouts", icon: CircleDollarSign },
      { to: "/ops/finance", label: "Finance", icon: PiggyBank },
    ],
  },
  {
    label: "Intel",
    items: [
      { to: "/ops/analytics", label: "Analytics", icon: BarChart3 },
      { to: "/ops/ai", label: "AI Assistant", icon: Sparkles },
    ],
  },
  {
    label: "System",
    items: [{ to: "/ops/settings", label: "Settings", icon: Settings }],
  },
];

const railLink = ({ isActive }) =>
  `relative flex items-center gap-3 h-10 rounded-lg px-[11px] shrink-0 transition-colors duration-150 ${
    isActive
      ? "bg-[var(--cc-accent)]/12 text-[var(--cc-accent)] after:absolute after:left-[-13px] after:top-2 after:bottom-2 after:w-[2px] after:rounded-full after:bg-[var(--cc-accent)]"
      : "text-muted-foreground hover:text-foreground hover:bg-secondary"
  }`;

export default function OpsLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [cmdOpen, setCmdOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [clock, setClock] = useState("");

  const isDispatcher = user?.role === "dispatcher";
  // Dispatchers get an operations-only view — Command + Pipeline, no finance/intel/system.
  const groups = isDispatcher ? NAV_GROUPS.filter((g) => g.label === "Command" || g.label === "Pipeline") : NAV_GROUPS;
  const flatNav = groups.flatMap((g) => g.items);
  const bottomNav = groups[0].items;

  useEffect(() => {
    const prev = document.title;
    document.title = isDispatcher ? "CastleOps — Dispatch" : "CastleOps — Mission Control";
    return () => { document.title = prev; };
  }, [isDispatcher]);

  useEffect(() => {
    const tick = () => setClock(new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }));
    tick();
    const t = setInterval(tick, 15000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const down = (e) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setCmdOpen((o) => !o);
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-background noise-cc ops-theme flex">
      {/* Icon rail — expands on hover */}
      <aside className="group hidden lg:flex flex-col w-16 hover:w-64 transition-[width] duration-300 ease-out border-r bg-card fixed inset-y-0 z-40 py-4 overflow-hidden hover:shadow-2xl">
        <NavLink to="/ops" end className="flex items-center gap-3 px-[13px] mb-5 shrink-0" data-testid="ops-rail-logo">
          <Logo size={26} />
          <span className="flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
            <span className="font-heading font-extrabold text-[13px] tracking-[0.24em] wordmark-metallic">CASTLEOPS</span>
            {isDispatcher && <span className="font-mono-cc text-[9px] tracking-[0.2em] text-[var(--cc-accent)] border border-[var(--cc-accent)]/40 rounded px-1 py-0.5">DISPATCH</span>}
          </span>
        </NavLink>
        <nav className="flex-1 flex flex-col px-3 overflow-y-auto overflow-x-hidden">
          {groups.map((g, gi) => (
            <div key={g.label} className="shrink-0">
              <div className={`flex items-center gap-2 px-[3px] ${gi === 0 ? "mb-1.5" : "mt-3.5 mb-1.5"}`}>
                <span className="h-px w-[22px] shrink-0 bg-border" />
                <span className="font-mono-cc text-[9px] tracking-[0.24em] uppercase text-muted-foreground whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200">{g.label}</span>
                <span className="h-px flex-1 bg-border opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
              </div>
              <div className="flex flex-col gap-1">
                {g.items.map((n) => (
                  <NavLink key={n.to} to={n.to} end={n.end} title={n.label}
                    data-testid={`ops-nav-${n.label.toLowerCase().replace(/ /g, "-")}`}
                    className={railLink}>
                    <n.icon className="w-[18px] h-[18px] shrink-0" />
                    <span className="text-[13px] font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200">{n.label}</span>
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>
        <button data-testid="ops-logout-button" onClick={handleLogout} title="Log out"
          className="mt-4 mx-3 flex items-center gap-3 h-10 rounded-lg px-[11px] shrink-0 text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors duration-150" aria-label="Log out">
          <LogOut className="w-[18px] h-[18px] shrink-0" />
          <span className="text-[13px] font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200">Log out</span>
        </button>
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="w-72 bg-card border-r h-full flex flex-col" style={{ paddingTop: "env(safe-area-inset-top)" }}>
            <div className="flex items-center justify-between px-5 h-16 border-b shrink-0">
              <div className="flex items-center gap-2.5">
                <Logo size={22} /><span className="font-heading font-extrabold text-[12px] tracking-[0.24em] wordmark-metallic">CASTLEOPS</span>
                {isDispatcher && <span className="font-mono-cc text-[8px] tracking-[0.2em] text-[var(--cc-accent)] border border-[var(--cc-accent)]/40 rounded px-1 py-0.5">DISPATCH</span>}
              </div>
              <button onClick={() => setMobileOpen(false)} aria-label="Close menu" className="p-2 -mr-2 text-muted-foreground" data-testid="ops-drawer-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto py-4 px-3">
              {groups.map((g) => (
                <div key={g.label} className="mb-4">
                  <p className="px-3 mb-1.5 font-mono-cc text-[10px] tracking-[0.24em] uppercase text-muted-foreground">{g.label}</p>
                  <div className="space-y-0.5">
                    {g.items.map((n) => (
                      <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setMobileOpen(false)}
                        className={({ isActive }) =>
                          `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-150 ${
                            isActive ? "bg-[var(--cc-accent)]/10 text-[var(--cc-accent)]" : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                          }`}>
                        <n.icon className="w-[18px] h-[18px] shrink-0" /> {n.label}
                      </NavLink>
                    ))}
                  </div>
                </div>
              ))}
            </nav>
            <button onClick={handleLogout} className="flex items-center gap-3 px-6 py-4 border-t text-sm text-muted-foreground"
              style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom))" }}>
              <LogOut className="w-4 h-4" /> Log out
            </button>
          </div>
          <div className="flex-1 bg-black/60 glass-cc" onClick={() => setMobileOpen(false)} />
        </div>
      )}

      <div className="flex-1 lg:ml-16 relative z-10 min-w-0">
        <header className="sticky top-0 z-30 bg-background/85 glass-cc border-b h-14 sm:h-16 flex items-center px-3 sm:px-4 lg:px-6 gap-2 sm:gap-3">
          <button className="lg:hidden p-2 -ml-1 shrink-0" data-testid="ops-mobile-menu" onClick={() => setMobileOpen(true)} aria-label="Menu">
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          <button
            data-testid="ops-command-palette-trigger"
            onClick={() => setCmdOpen(true)}
            className="flex items-center gap-2 text-xs text-muted-foreground border rounded-full px-3 py-2 hover:border-[var(--cc-accent)] transition-colors duration-150 min-w-0 sm:w-52"
          >
            <Search className="w-3.5 h-3.5 shrink-0" />
            <span className="flex-1 text-left hidden sm:block">Search...</span>
            <kbd className="font-mono-cc text-[10px] border rounded px-1.5 py-0.5 hidden sm:inline">⌘K</kbd>
          </button>
          <div className="absolute left-1/2 lg:left-[calc(50%-32px)] -translate-x-1/2 pointer-events-none hidden md:block" data-testid="ops-header-wordmark">
            <span className="font-heading font-extrabold text-[16px] tracking-[0.34em] wordmark-metallic">CASTLEOPS</span>
          </div>
          <div className="flex-1" />
          <span className="hidden xl:block font-mono-cc text-[11px] tracking-[0.18em] text-muted-foreground tabular-nums" data-testid="ops-header-clock">{clock}</span>
          <span className="hidden xl:flex items-center gap-2 overline-cc">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 pulse-dot" /> Operational
          </span>
          <NotificationBell />
          {isDispatcher && (
            <span data-testid="ops-role-badge" className="hidden sm:flex items-center gap-1.5 rounded-full border border-[var(--cc-accent)]/40 bg-[var(--cc-accent)]/10 text-[var(--cc-accent)] px-2.5 py-1 font-mono-cc text-[10px] tracking-[0.14em] uppercase shrink-0">
              <ShieldCheck className="w-3.5 h-3.5" /> Dispatcher
            </span>
          )}
          <div className="hidden sm:block"><ThemeToggle /></div>
          <Button data-testid="ops-view-site-button" variant="outline" size="sm" className="rounded-full hidden md:inline-flex" onClick={() => navigate("/")}>
            View Site
          </Button>
          <div className="flex items-center gap-2 border rounded-full pl-1 pr-2 sm:pr-3 py-1 shrink-0" data-testid="ops-user-chip">
            {user?.picture ? (
              <img src={user.picture} alt={user.name} className="w-7 h-7 rounded-full object-cover" />
            ) : (
              <div className="w-7 h-7 rounded-full bg-[var(--cc-accent)]/15 text-[var(--cc-accent)] flex items-center justify-center font-heading font-bold text-xs">
                {user?.name?.[0] || "A"}
              </div>
            )}
            <span className="text-xs font-semibold hidden md:block">{user?.name?.split(" ")[0]}</span>
          </div>
        </header>
        <main className="p-4 lg:p-8 pb-28 lg:pb-8 min-w-0"><Outlet /></main>
      </div>

      {/* Mobile bottom command bar */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 border-t bg-background/88 glass-cc" style={{ paddingBottom: "env(safe-area-inset-bottom)" }} data-testid="ops-bottom-nav">
        <div className="grid grid-cols-5 h-16">
          {bottomNav.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end}
              data-testid={`ops-bottom-nav-${n.label.toLowerCase().replace(/ /g, "-")}`}
              className={({ isActive }) =>
                `relative flex flex-col items-center justify-center gap-1 min-w-0 transition-colors duration-150 ${
                  isActive
                    ? "text-[var(--cc-accent)] before:absolute before:top-0 before:left-1/2 before:-translate-x-1/2 before:w-8 before:h-[2px] before:bg-[var(--cc-accent)]"
                    : "text-muted-foreground"
                }`}>
              <n.icon className="w-5 h-5" />
              <span className="font-mono-cc text-[9px] tracking-[0.12em] uppercase truncate max-w-full px-1">{n.label.split(" ")[0]}</span>
            </NavLink>
          ))}
          <button onClick={() => setMobileOpen(true)} data-testid="ops-bottom-nav-more"
            className="flex flex-col items-center justify-center gap-1 text-muted-foreground min-w-0">
            <Menu className="w-5 h-5" />
            <span className="font-mono-cc text-[9px] tracking-[0.12em] uppercase">More</span>
          </button>
        </div>
      </nav>

      <CommandDialog open={cmdOpen} onOpenChange={setCmdOpen}>
        <CommandInput placeholder="Jump to a section or action..." data-testid="command-palette-input" />
        <CommandList>
          <CommandEmpty>No results found.</CommandEmpty>
          <CommandGroup heading="Navigate">
            {flatNav.map((n) => (
              <CommandItem key={n.to} onSelect={() => { navigate(n.to); setCmdOpen(false); }} data-testid={`command-item-${n.label.toLowerCase().replace(/ /g, "-")}`}>
                <n.icon className="w-4 h-4 mr-2" /> {n.label}
              </CommandItem>
            ))}
          </CommandGroup>
          <CommandGroup heading="Actions">
            <CommandItem onSelect={() => { navigate("/"); setCmdOpen(false); }}>View Public Site</CommandItem>
            <CommandItem onSelect={handleLogout}>Log Out</CommandItem>
          </CommandGroup>
        </CommandList>
      </CommandDialog>

      {!isDispatcher && (
        <AISidePanel mode="admin"
          intro="Standing by, commander. Ask me for a quote, a dispatch call, or a rundown of today's ops."
          quickActions={[
            { label: "Today's briefing", prompt: "Give me a 3-line briefing on today's operations." },
            { label: "Quote for 3-seater couch RSF→Oceanside", prompt: "Quote a white-glove delivery for one 3-seater couch from Rancho Santa Fe to Oceanside, no stairs." },
            { label: "Idle crew", prompt: "Who's available right now?" },
          ]} />
      )}
    </div>
  );
}
